/**
 * Sprint T-16.1 · Automation Center · OverviewTab.
 *
 * Vista consolidada · 5 hooks concurrentes (§17.1 approved).
 * Cada widget carga y falla de forma independiente para preservar desacoplamiento.
 */
import React from "react";
import { Activity, AlertOctagon, Cog, Gauge, ShieldCheck } from "lucide-react";
import { WidgetCard, WidgetGrid, MetricCard, EmptyState, LoadingState, CitationBadge } from "../advisor";
import {
  useAutomationInfo, useAutomationMetrics, useExecutions,
  useDlq, useEvents,
} from "../../hooks/useAutomation";
import { StatusBadge } from "./shared/Badges";
import { RelativeTime, AbsoluteTime } from "./shared/Formatters";
import { EventRow } from "./shared/DomainCards";


function EngineHealthCard() {
  const q = useAutomationInfo();
  const d = q.data;
  const healthy = Boolean(d?.running);
  return (
    <WidgetCard
      title="Engine health"
      description={`version ${d?.engine_version || "—"}`}
      citations={["/api/automation/info"]}
      testId="widget-engine-health"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="/info no responde" testId="engine-health-error" />}
      {d && (
        <div className="space-y-2" data-testid="engine-health-ok">
          <div className="flex items-center gap-2">
            <div
              className={`h-2.5 w-2.5 rounded-full ${
                healthy ? "bg-emerald-500 animate-pulse" : "bg-red-500"
              }`}
              aria-hidden
            />
            <span className="text-sm font-semibold" data-testid="engine-health-status">
              {healthy ? "RUNNING" : "STOPPED"}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Iniciado</dt>
              <dd><RelativeTime iso={d.started_at} /></dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Último tick</dt>
              <dd data-testid="engine-last-tick"><RelativeTime iso={d.scheduler?.last_tick_at} /></dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Ticks</dt>
              <dd className="font-mono">{d.scheduler?.tick_count ?? 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Procesadas</dt>
              <dd className="font-mono">{d.worker?.processed ?? 0}</dd>
            </div>
          </dl>
        </div>
      )}
    </WidgetCard>
  );
}


function CatalogSummaryCard() {
  const q = useAutomationInfo();
  const d = q.data?.catalog;
  return (
    <WidgetCard title="Catálogo" citations={["/api/automation/info"]} testId="widget-catalog-summary">
      {q.isLoading && <LoadingState />}
      {d && (
        <div className="space-y-2" data-testid="catalog-summary-ok">
          <div className="text-2xl font-semibold tabular-nums" data-testid="catalog-total">
            {d.total} <span className="text-xs text-[var(--ag-text-dim)]">jobs registrados</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(d.by_category || {}).map(([cat, n]) => (
              <span
                key={cat}
                className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] px-1.5 py-0.5 text-[10px]"
              >
                <span className="text-[var(--ag-text-dim)]">{cat}</span>{" "}
                <span className="font-mono">{n}</span>
              </span>
            ))}
          </div>
          <div className="text-[10px] text-[var(--ag-text-dim)]">
            with_deps: <span className="font-mono">{d.with_deps || 0}</span> · uses_llm:{" "}
            <span className="font-mono">{d.uses_llm || 0}</span>
          </div>
        </div>
      )}
    </WidgetCard>
  );
}


function RunningExecutionsMini() {
  const q = useExecutions({ status: "running", limit: 5 });
  const items = q.data?.items || [];
  return (
    <WidgetCard
      title="En ejecución"
      description="Ejecuciones activas ahora mismo"
      citations={["/api/automation/executions?status=running"]}
      testId="widget-running-mini"
    >
      {q.isLoading && <LoadingState />}
      {q.data && items.length === 0 && (
        <div data-testid="running-mini-empty" className="text-[11px] text-[var(--ag-text-dim)]">
          Sin ejecuciones activas.
        </div>
      )}
      {items.length > 0 && (
        <ul className="space-y-1" data-testid="running-mini-list">
          {items.slice(0, 5).map((e) => (
            <li
              key={e.id}
              className="flex items-center justify-between rounded border border-blue-500/30 bg-blue-500/5 px-2 py-1 text-[10px]"
            >
              <span className="font-mono">{e.job_name}</span>
              <RelativeTime iso={e.started_at} />
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}


function DlqAlertBanner({ onGoToDlq }) {
  const q = useDlq({ limit: 5 });
  const total = q.data?.total || 0;
  if (q.isLoading || !q.data || total === 0) return null;
  return (
    <div
      data-testid="dlq-alert-banner"
      className="flex items-center justify-between rounded-lg border border-red-500/40 bg-red-500/10 p-3"
    >
      <div className="flex items-center gap-2">
        <AlertOctagon className="h-4 w-4 text-red-500" />
        <span className="text-sm">
          <strong data-testid="dlq-alert-count">{total}</strong> ejecuciones en Dead Letter Queue requieren revisión
        </span>
      </div>
      <button
        type="button"
        data-testid="dlq-alert-cta"
        onClick={onGoToDlq}
        className="rounded border border-red-500/60 bg-red-500/20 px-3 py-1 text-xs font-medium text-red-500 hover:bg-red-500/30"
      >
        Ver DLQ →
      </button>
    </div>
  );
}


function MetricsSummaryStrip() {
  const q = useAutomationMetrics();
  const counts = q.data?.live?.counts || {};
  return (
    <WidgetGrid cols="md:grid-cols-4 lg:grid-cols-4">
      <MetricCard label="started" value={counts.started ?? 0} testId="metric-started" hint="acumulado" />
      <MetricCard label="success" value={counts.success ?? 0} testId="metric-success" hint="OK" />
      <MetricCard label="failed" value={counts.failed ?? 0} testId="metric-failed" hint="con error" />
      <MetricCard label="dlq" value={counts.dlq ?? 0} testId="metric-dlq" hint="en dead letter" />
    </WidgetGrid>
  );
}


function RecentEventsList() {
  const q = useEvents({ limit: 10 });
  const items = q.data?.items || [];
  return (
    <WidgetCard
      title="Eventos recientes"
      description="Últimos 10 · sin filtro"
      citations={["/api/automation/events"]}
      testId="widget-recent-events"
    >
      {q.isLoading && <LoadingState />}
      {q.data && items.length === 0 && (
        <EmptyState message="Sin eventos registrados aún." testId="recent-events-empty" />
      )}
      {items.length > 0 && (
        <ul className="space-y-0.5" data-testid="recent-events-list">
          {items.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}


export default function OverviewTab({ onNavigate }) {
  return (
    <section className="space-y-4" data-testid="automation-overview-tab">
      <DlqAlertBanner onGoToDlq={() => onNavigate?.("dlq")} />

      <WidgetGrid cols="lg:grid-cols-3">
        <EngineHealthCard />
        <CatalogSummaryCard />
        <RunningExecutionsMini />
      </WidgetGrid>

      <MetricsSummaryStrip />

      <RecentEventsList />

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["T-16 backend · 14 endpoints", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
